import { z } from 'zod/v3';

// ---------------------------------------------------------------------------
// CheckVariable — mirrors CheckVariable from server adapters/types, serializable for client
// ---------------------------------------------------------------------------

export const CheckVariableOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
});

export const ActionCatalogCheckVariableSchema = z.object({
  id: z.string(),
  label: z.string(),
  type: z.enum(['text', 'number', 'boolean', 'select', 'multi-select']),
  required: z.boolean().optional(),
  default: z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]).optional(),
  helpText: z.string().optional(),
  placeholder: z.string().optional(),
  options: z.array(CheckVariableOptionSchema).optional(),
});
export type ActionCatalogCheckVariable = z.infer<typeof ActionCatalogCheckVariableSchema>;

// ---------------------------------------------------------------------------
// ActionCatalogEntryDTO — one check from the registry
// ---------------------------------------------------------------------------

export const ActionCatalogEntryDTOSchema = z.object({
  /** Unique check ID, e.g. "github.branch-protection" */
  checkId: z.string(),
  /** Provider key, e.g. "github" */
  provider: z.string(),
  /** Human-readable provider name, e.g. "GitHub" */
  providerLabel: z.string(),
  /** Action/check name within the provider, e.g. "branch-protection" */
  action: z.string(),
  /** Human-readable check name, e.g. "Branch Protection Enabled" */
  name: z.string(),
  /** Check description */
  description: z.string(),
  /** User-configurable variables for parameterizing the check */
  variables: z.array(ActionCatalogCheckVariableSchema),
  /** SOC 2 control IDs this check maps to, e.g. ["CC6.1", "CC6.6"] */
  controlMappings: z.array(z.string()),
  /** Whether the provider is currently connected (has an active integration) */
  connected: z.boolean(),
});
export type ActionCatalogEntryDTO = z.infer<typeof ActionCatalogEntryDTOSchema>;

// ---------------------------------------------------------------------------
// ActionCatalogProviderDTO — all checks grouped by provider
// ---------------------------------------------------------------------------

export const ActionCatalogProviderDTOSchema = z.object({
  /** Provider key, e.g. "github" */
  provider: z.string(),
  /** Human-readable provider name, e.g. "GitHub" */
  providerLabel: z.string(),
  /** Icon identifier or emoji for the provider (client-side rendering) */
  icon: z.string(),
  /** Whether this provider has an active integration connection */
  connected: z.boolean(),
  /** All checks available for this provider */
  actions: z.array(ActionCatalogEntryDTOSchema),
});
export type ActionCatalogProviderDTO = z.infer<typeof ActionCatalogProviderDTOSchema>;

// ---------------------------------------------------------------------------
// ActionCatalogResponseDTO — top-level response
// ---------------------------------------------------------------------------

export const ActionCatalogResponseDTOSchema = z.object({
  providers: z.array(ActionCatalogProviderDTOSchema),
});
export type ActionCatalogResponseDTO = z.infer<typeof ActionCatalogResponseDTOSchema>;
